import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { startCase } from 'shared/utils/startCase';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { t } from 'localization/utils/t';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { FeatureFlags } from 'featureFlags/constants';
import { getConsumerSearchServerSideData } from 'server/utils/getConsumerSearchDataServerSide';
import QueryString from 'qs';

const TP = 'pages.sneakers.technology.technologyValue';

interface ITechnologyNutritionFactPage extends IProductLandingPage {
  metaDescription?: string;
}

const TechnologyNutritionFactPage = ({
  breadcrumbs,
  browseKey,
  browseValue,
  constructorResponse,
  consumerSearchFilters,
  consumerSearchInitialData = '{}',
  displayName,
  globalPricingId,
  technologyValue,
  isCrawler = false,
}: ITechnologyNutritionFactPage) => {
  const {
    name = displayName,
    description = '',
    metadataDescription = '',
    titleTag = '',
  } = breadcrumbs || {};

  return (
    <PLPLandingPageComponent
      breadcrumbs={[
        { name: t(`${TP}.sneakers`, 'Sneakers'), slug: `/sneakers` },
        {
          name: startCase(technologyValue),
          slug: `/`,
          isStatic: true,
        },
      ]}
      browseKey={browseKey}
      browseValue={browseValue}
      constructorResponse={constructorResponse}
      consumerSearchFilters={consumerSearchFilters}
      consumerSearchInitialData={JSON.parse(consumerSearchInitialData)}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      metadataDescription={metadataDescription}
      metaTitle={titleTag}
      isCrawler={isCrawler}
    />
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const { technologyValue }: { technologyValue: string } = query;
  const q = QueryString.parse(query);
  const displayName: string = startCase(technologyValue);
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const isCrawler = isSearchCrawler(req?.headers);
  const isConsumerSearchEnable =
    req.featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND];

  const consumerSearchFilters = {
    technologies: technologyValue,
    categories: 'footwear',
  };

  const breadcrumbs = await getBreadcrumbs({
    req,
    res,
    path: `${BREADCRUMBS.SNEAKERS}/technology/${technologyValue}`,
  });

  if (isConsumerSearchEnable) {
    const consumerSearchInitialData = await getConsumerSearchServerSideData({
      filters: {
        ...q,
        pageFilters: {
          technologies: technologyValue,
          categories: 'footwear',
        },
      },
    });

    if (!consumerSearchInitialData?.items?.hasData) {
      return { notFound: true };
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        breadcrumbs,
        browseKey: 'group_id',
        browseValue: 'sneakers',
        consumerSearchFilters,
        consumerSearchInitialData: JSON.stringify({}),
        displayName,
        globalPricingId,
        technologyValue,
        isCrawler,
      },
    };
  }

  try {
    const browseResponse = await getBrowseResults({
      req,
      res,
      resolvedUrl,
      query,
      filterKey: 'group_id',
      filterValue: 'sneakers',
    });

    const invalidBrowse = browseResponse.response.total_num_results === 0;

    if (invalidBrowse) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        breadcrumbs,
        browseKey: 'group_id',
        browseValue: 'sneakers',
        constructorResponse: browseResponse,
        consumerSearchFilters,
        displayName,
        globalPricingId,
        technologyValue,
        isCrawler,
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};

export default TechnologyNutritionFactPage;
